import React, { useMemo } from 'react';
import PropTypes from "prop-types";
import { useDispatch, useSelector } from 'react-redux';

import { CreativeApprovals } from 'reducers/CreativeApprovals';
import { closeModal } from 'reducers/Modal';
import { useTooltipRebuild } from 'hooks/useTooltipRebuild';
import { safeDecodeURIComponent } from 'utils/common';

import EmptySection from "widgets/EmptySection";
import {
	Blade,
	Button,
	DataGrid,
	Pager,
	ProgressIndicator
} from "rhombus-react";
import Measure from 'react-measure';

import useActiveCreative from 'hooks/useActiveCreative';

import {
	MODAL_TYPES,
	CREATIVE_ROW_COUNT_OPTIONS,
	MIN_GRID_HEIGHT,
	DEFAULT_ROW_HEIGHT
} from 'utils/constants';

import { CREATIVE_APPROVAL_COLUMNS } from "widgets/grid/columns/CreativeApprovals";

import { useCollectionFiltering, useGridScrollUtils } from "utils/DataGrid";

import "./ApprovalHistory.css";

export function HistoryGrid({ libraryId, creativeIds }){
	const dispatch = useDispatch();
	const approvals = useSelector(state => state.creativeApprovals);
	const { isLoaded, items = []} = approvals || {};

	const dataGridRef = useGridScrollUtils([approvals]);

	const mappedApprovals = useMemo(() => {
		return items.map((item) => {
			/*
				The `key` names here are significant because they map to internal fields that the API uses
				for the `sort` parameter, case-sensitive, as it doesn't currently support sorting by nested
				object properties.
			*/

			return {
				creativeName: safeDecodeURIComponent(item.creative.name),
				creativeId: item.creative.id,
				name: item.approval,
				dateCreated: item.dateCreated,
				username: item?.user?.name,
				notes: item.notes,
			};
		});
	}, [items]);

	const {
		filterInfo: { page, rpp, sort },
	} = useCollectionFiltering(
		approvals,
		CreativeApprovals.setAdvancedFilter,
		() => {
			if(libraryId && creativeIds){
				dispatch(CreativeApprovals.fetchApprovalHistory(libraryId, creativeIds));
			}
		}
	);

	const emptyProps = {
		className: "empty-approval-history",
		header: "No approval history for this creative",
		src: "/images/notification-icon.svg",
	};

	useTooltipRebuild(items);

	if (isLoaded && items.length === 0){
		return(
			<EmptySection key="empty" {...emptyProps} />
		);
	}

	const [sortColumn, sortDirection] = sort.split(":");

	function getApprovalRow(rowId){
		return mappedApprovals[rowId];
	}

	const dataGridProps = {
		columns: CREATIVE_APPROVAL_COLUMNS,
		rowGetter:  getApprovalRow,
		rowsCount:  mappedApprovals.length,
		rowHeight:  DEFAULT_ROW_HEIGHT,
		sortColumn,
		sortDirection,
		onGridSort: (col, dir) => dispatch(CreativeApprovals.setSort(col, dir)),
	};

	const pagerProps = {
		showItemCount: true,
		initialPage: page,
		forcePage: page,
		pageSize: rpp,
		pageCount: approvals.totalPages,
		maxItems: approvals.totalItems,
		showItemWord: "",
		rowCountItems: CREATIVE_ROW_COUNT_OPTIONS,

		disabled: !approvals.isLoaded,

		onPageChange: newPage => dispatch(CreativeApprovals.setPage(newPage)),
		onRowCountChange: newRPP => dispatch(CreativeApprovals.setRPP(newRPP)),
	};

	return(
		<div className="approval-history-container">
			<ProgressIndicator isLoading={!isLoaded} />
			<div className="approval-history">
				<Measure>
					{(obj) => {
						let gridHeight = obj?.contentRect?.entry?.height || MIN_GRID_HEIGHT;

						return (
							<div className="measure-component" ref={obj.measureRef}>
								<div ref={dataGridRef}>
									<DataGrid
										{...dataGridProps}
										minHeight={gridHeight}
									/>
								</div>
							</div>
						);
					}}
				</Measure>
				<Pager {...pagerProps} />
			</div>
		</div>
	);
}
HistoryGrid.displayName = "HistoryGrid";
HistoryGrid.propTypes = {
	libraryId: PropTypes.number.isRequired,
	creativeIds: PropTypes.string.isRequired,
};

function ApprovalHistoryModal() {
	const {
			isOpen,
			modalOptions,
			modalType,
		} = useSelector(state => ({
			isOpen: state.modal.isOpen,
			modalOptions: state.modal.modalOptions,
			modalType: state.modal.modalType,
		}))
		, { libraryId, creatives = []} = modalOptions || {}
		, dispatch = useDispatch();

	useActiveCreative(creatives[0]?.id);
	/*
		This component is always rendered when the Creatives grid is open, but only displays the modal
		depending on `modalOptions`.

		Short return if we aren't in the right modal
	*/
	const thisModal = modalType === MODAL_TYPES.APPROVAL_HISTORY;
	if(!(isOpen) || !thisModal){
		return null;
	}

	const creativeIds = creatives?.map((creative) => {
		return creative.id;
	});

	let modalTitle = creatives?.length > 1 ? `${creatives?.length} Creatives` : safeDecodeURIComponent(creatives?.[0].name);


	let bladeProps = {
		verticallyCenter: true,
	};

	const onClose = () =>{
		dispatch(closeModal());
	};

	const renderContent = () => {
		return (
			<HistoryGrid
				libraryId = {libraryId}
				creativeIds = {creativeIds.join(',')}
			/>
		);
	};

	const renderFooter = () => {
		return (
			<Button className="close-button" theme="grey" onClick={ onClose }>Close</Button>
		);
	};


	return (
		<Blade
			type="modal"
			title={`Approval History - ${modalTitle}`}
			className="approval-history-modal"
			allowFullScreen={false}
			xClose={true}
			bladeProps={bladeProps}
			onClose={onClose}
			renderContent={renderContent}
			renderFooter = {renderFooter} />
	);
}

ApprovalHistoryModal.displayName = "ApprovalHistoryModal";

export default ApprovalHistoryModal;
